import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  IconButton,
  Toolbar,
  Typography,
  InputBase,
  CircularProgress,
  Theme,
  makeStyles,
  createStyles,
  fade,
} from "@material-ui/core";
import {
  Colors,
  AppContainerStyle,
  Spacing,
} from "../theme/Theme";
import SearchIcon from "@material-ui/icons/Search";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useHistory, useLocation } from "react-router-dom";
import { TopAppBarHeight } from "../theme/ComponentTheme";
import { RBContext } from "./RBContext";
import EditLocationSharpIcon from '@material-ui/icons/EditLocationSharp';
import { UpdateLocationModal } from "../components/UpdateLocationModal";
import {
  RegionalLocationDropdown,
} from "./RegionalLocationDropdown";
import { EmptyString } from "../util/Common";
import { AccountInfoContext } from "./AccountInfoContext";
import { getRegionalManagerOwnerDisplayName } from "../interfacesProps/RegionalManagerDashboardProps";
import { renderRBPayText } from "./RBPayWordmark";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: "white",
      height: TopAppBarHeight,
      borderBottomColor: AppContainerStyle.borderColor,
      borderBottomStyle: "solid",
      borderBottomWidth: 1,
      zIndex: theme.zIndex.drawer + 1,
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    grow: {
      flexGrow: 1,
    },
    search: {
      position: "relative",
      borderRadius: theme.shape.borderRadius,
      borderColor: Colors.darkGrey,
      borderStyle: "solid",
      borderWidth: 1,
      backgroundColor: fade(theme.palette.common.white, 0.15),
      "&:hover": {
        backgroundColor: fade(theme.palette.common.white, 0.25),
      },
      marginRight: theme.spacing(2),
      marginLeft: 0,
      width: "100%",
      [theme.breakpoints.up("sm")]: {
        marginLeft: theme.spacing(3),
        width: "auto",
      },
    },
    searchIcon: {
      padding: Spacing.small,
      height: "100%",
      position: "absolute",
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: Colors.themeBlue,
    },
    inputRoot: {
      color: Colors.black,
    },
    inputInput: {
      padding: theme.spacing(1, 1, 1, 0),
      // vertical padding + font size from searchIcon
      paddingLeft: `calc(1em + ${theme.spacing(3)}px)`,
      transition: theme.transitions.create("width"),
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "20ch",
      },
    },
  })
);

interface RBAppbarProps {
  barName: any;
  deeperLevel?: boolean;
  searchable?: boolean;

  barChildren?: any;
} 

export const RBAppbar: React.FC<RBAppbarProps> = ({ ...props }) => {
  const { primaryColor,secondaryColor, industryMode } = useContext(RBContext);
  const { accountInformation, isPageLoading } = useContext(AccountInfoContext);
  const history = useHistory();
  const classes = useStyles();
  const location = useLocation();
  const onArrowBackIconClick = () => {
    history.goBack();
  };
  
  const roleName = localStorage.getItem("roleName");
  const [open, setOpen] = useState(false);
  const isRegiounalManager = roleName === "regionalmanager";
  const [selectedRegionalLocation, setSelectedRegionalLocation] = useState(
    localStorage.getItem("regionalManagerSelectedLocationTitle") || EmptyString
  );
  
  const ownerDefaultLocationName =
    accountInformation?.pharmacies?.find(
      (pharmacy: any) => pharmacy.id === accountInformation?.owner?.defaultPharmacyId
    )?.name || EmptyString;
    
  const ownerDisplayName = ownerDefaultLocationName || localStorage.getItem("pharmacyName") || EmptyString;
  
  const displayName = 
  isRegiounalManager
    ? selectedRegionalLocation
    : roleName === "owner"
      ? ownerDisplayName
      : localStorage.getItem("firstName");

  const onRegionalLocationSelect = (row: any) => {

    console.log("onRegionalLocationSelect", row);

    setSelectedRegionalLocation(row.locationTitleName);
    localStorage.setItem("regionalManagerSelectedUserId", row.ownerUserId);
    localStorage.setItem("regionalManagerSelectedLocationId", row.locationId);
    localStorage.setItem(
      "regionalManagerSelectedLocationTitle",
      row.locationTitleName
    );
    localStorage.setItem(
      "regionalManagerSelectedOwnerName",
      getRegionalManagerOwnerDisplayName(row)
    );
    localStorage.setItem("defaultPharmacyId", row.locationId);
    localStorage.setItem("pharmacyName", row.pharmacy?.name || row.locationTitleName);
    window.dispatchEvent(new Event("regional-manager-location-changed"));

  };

  useEffect(() => {
    const syncRegionalLocation = () => {
      setSelectedRegionalLocation(
        localStorage.getItem("regionalManagerSelectedLocationTitle") || EmptyString
      );
    };

    window.addEventListener(
      "regional-manager-location-changed",
      syncRegionalLocation
    );

    return () => {
      window.removeEventListener(
        "regional-manager-location-changed",
        syncRegionalLocation
      );
    };
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const isDashboardPage = location.pathname === "/dashboard";
  const showRegionalManagerDropdownInTitle =
    isDashboardPage && isRegiounalManager;
  const renderedBarName = typeof props.barName === "string" ? renderRBPayText(props.barName) : props.barName;

  return (
    <AppBar position="sticky" elevation={0} className={classes.appBar}>
      <div style={isDashboardPage && (roleName === "owner" || roleName === "regionalmanager") ? { display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", position: "relative" } : {}}>
      <Toolbar>
        {props.deeperLevel && (
          <IconButton
            edge="start"
            style={{ color: primaryColor }}
            className={classes.menuButton}
            onClick={onArrowBackIconClick}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {showRegionalManagerDropdownInTitle ? (
          <RegionalLocationDropdown
            primaryColor={primaryColor}
            selectedLocationTitle={selectedRegionalLocation}
            onSelectLocation={onRegionalLocationSelect}
            triggerLabel="Change Office"
          />
        ) : props.barName === 'Change Pharmacy' ? 
          <Typography variant="h6" noWrap style={{ color: primaryColor }}>Change Office
          </Typography> :
          <Typography variant="h6" noWrap style={{ color: primaryColor }}>
            {renderedBarName}
          </Typography>
        }
        
        {props.searchable && (
          <div
            className={classes.search}
            style={{ display: "flex", flexDirection: "row" }}
          >
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
            <InputBase
              placeholder="Search…"
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              inputProps={{ "aria-label": "search" }}
            />
          </div>
        )}
        <div className={classes.grow} />
        {props.barChildren}
      </Toolbar>
      {isDashboardPage && (roleName === "owner" || roleName === "regionalmanager") && (
        <div style={{ display: "flex", alignItems: "center", marginRight: "16px" }}>
          {isRegiounalManager && !showRegionalManagerDropdownInTitle && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "10px",
                gap: "8px",
                position: "relative",
              }}
            >
              <RegionalLocationDropdown
                primaryColor={primaryColor}
                selectedLocationTitle={selectedRegionalLocation}
                onSelectLocation={onRegionalLocationSelect}
                triggerLabel="Change Office"
              />
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              cursor: isRegiounalManager ? "default" : "pointer",
            }}
            onClick={!isRegiounalManager ? handleOpen : undefined}
          >
            <IconButton style={{ color: primaryColor, transform: "scale(1.2)" }}>
              <EditLocationSharpIcon />
            </IconButton>
            <Typography variant="body1" style={{ color: primaryColor }}>
              {displayName}
            </Typography>
            {!isRegiounalManager && roleName === "owner" && isPageLoading && (
              <CircularProgress
                size={14}
                style={{ color: primaryColor, marginLeft: 8 }}
              />
            )}
          </div>
        </div>
      )}
      </div>
     <UpdateLocationModal open={open} handleClose={handleClose} />
    </AppBar>
  );
};
